import React, { useReducer, useState, useEffect } from 'react';
import { FooterSection, FaqSection, BreadCrumb } from '../components';

import { TrainHeader, TrainDetails, TrainStationsList, TrainStationAddress, TrainInfoList } from './components';
import DataLoaderError from '../../errors/DataLoaderError';
import TrainInfoContext from './TrainInfoContext';
import { TrainInfoData, EnvironmentVariables } from '../types';
import { getAppEnv } from '../../../config/env';
import { RouteComponentProps } from 'react-router-dom';
import AdWrapper from '../components/AdWrapper';
import { AD_PAGES } from '../../utils/adsUtils';
import { RECOMMEND_PATH } from '../components/RecommendTripPathList/utils/recommendTripPathListUtils';
import RecommendTripPathWrapper from '../components/RecommendTripPathList/RecommendTripPathList';
import { searchWidgetInitialState, searchWidgetReducer } from '../../reducers/searchWidgetReducer';
import SearchWidget from '../components/SearchWidget/SearchWidget';
import SEARCH_WIDGET_UTILS from '../components/SearchWidget/utils/utils';
import HtmlHeader from '../components/HtmlHeader';
import AppDownloadBanner from '../components/AppDownloadBanner';
import { SEO_TRAIN_PAGE } from '../../utils/RailsSEOConstants';
import { isMobile } from '../../utils/commonUtils';
import {
  getOtherDestFromCityInterlinkingBlocks,
  getOtherSrcToCityInterlinkingBlocks
} from '../route_info/RouteInfoUtils';

const { Provider: TrainInfoProvider } = TrainInfoContext;
const SearchWidgetContext = React.createContext<any>(searchWidgetInitialState);

const env = getAppEnv();

const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;

type PathParamsType = {
  trainId: string;
};

type TrainInfoProps = RouteComponentProps<PathParamsType> & {
  data: TrainInfoData;
};

function decorateTrainData(trainData: TrainInfoData) {
  let {
    otherDestinationsFromSource,
    otherSourcesToDestination,
    otherDestinationsFromDestination,
    otherSourcesToSource
  } = trainData;
  const { tokens } = trainData;
  const toCity = tokens.destination;
  const fromCity = tokens.source;
  otherSourcesToDestination = getOtherSrcToCityInterlinkingBlocks(otherSourcesToDestination, toCity);
  otherDestinationsFromSource = getOtherDestFromCityInterlinkingBlocks(otherDestinationsFromSource, fromCity);
  otherDestinationsFromDestination = getOtherDestFromCityInterlinkingBlocks(otherDestinationsFromDestination, toCity);
  otherSourcesToSource = getOtherSrcToCityInterlinkingBlocks(otherSourcesToSource, fromCity);

  return {
    ...trainData,
    otherDestinationsFromSource,
    otherSourcesToDestination,
    otherDestinationsFromDestination,
    otherSourcesToSource
  };
}

export function TrainInfo(props: TrainInfoProps) {
  const [stickyHeader, setStickyHeader] = useState(false);
  const [searchWidgetState, searchWidgetDispatch] = useReducer(searchWidgetReducer, searchWidgetInitialState);
  const { onTrainClicked, updateCityNameWithFilter, onMountInitSearchWidgetWrapper } =
    SEARCH_WIDGET_UTILS.TRAIN_INFO_UTILS(searchWidgetDispatch);
  const { fromFilter, toFilter } = searchWidgetState;
  useEffect(() => {
    updateCityNameWithFilter({ fromFilter, toFilter, data });
  }, [fromFilter, toFilter]);
  const handleScroll = () => {
    // to handle all browsers https://stackoverflow.com/a/20478983/3809168
    const scrollOffset = Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop); // to handle
    if (scrollOffset >= 100) {
      setStickyHeader(true);
    } else {
      setStickyHeader(false);
    }
  };
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const { data } = props;

  function getAdsConditionally() {
    const displayStationAdForRoute = data['displayStationAdForRoute'];
    if (displayStationAdForRoute) {
      return (
        <div className="makeFlex perfectCenter appendBottom20">
          {isMobile() && (
            <AdWrapper adsPageName={AD_PAGES.RAIL_TRAINS_SEO_TRAIN_TEMPLATE_MOBILE_THIRD_FOLD.adsPageName} />
          )}
        </div>
      );
    }
    return null;
  }

  return (
    <TrainInfoProvider
      value={{
        ...data,
        onTrainClicked: onTrainClicked
      }}
    >
      <AppDownloadBanner {...data.template} />
      <SearchWidgetContext.Provider value={{ searchWidgetState, searchWidgetDispatch }}>
        <SearchWidget
          openDatePickerOnMount={true}
          topFix={true}
          templateId={data.templateId}
          searchButtonTitle={data.miscellaneous['SELECT DATE AND CHECK AVAILABILITY']}
          onMountInitSearchWidget={onMountInitSearchWidgetWrapper(data)}
          pageWiseContext={SearchWidgetContext}
          pageName={SEO_TRAIN_PAGE}
        />
      </SearchWidgetContext.Provider>
      <div className="container">
        <HtmlHeader data={data} containsAlternateHref={true} />
        <div className="railSeoWrapper">
          <BreadCrumb breadcrumb={data.template.breadcrumb} />
        </div>
      </div>
      <div className={'stickyTrainInfoHeader ' + (stickyHeader ? 'stickyTrainInfoHeaderShadow' : '')}>
        <div className="container">
          <TrainHeader
            sourceStation={data.sourceStation}
            destinationStation={data.destinationStation}
            template={data.template}
            onClick={onTrainClicked}
            trainNumber={data.trainInfo.obj ? data.trainInfo.obj.trainNumber : ''}
          />
        </div>
      </div>
      <div className="container">
        <div className="railSeoWrapper">
          <main>
            <TrainDetails
              trainInfo={data.trainInfo.obj}
              title={data.trainInfo.title}
              sourceStation={data.sourceStation}
              destinationStation={data.destinationStation}
              alternatePageUrl={data.alternatePageUrlInfo}
              onClick={onTrainClicked}
            />
            <div className="appendBottom20 makeFlex perfectCenter">
              <AdWrapper adsPageName={AD_PAGES.TRAIN_INFO_PAGE.adsPageName} />
            </div>
            {data.primaryTrainRoute && data.primaryTrainRoute.list.length > 0 && (
              <TrainStationsList
                stations={data.primaryTrainRoute.list}
                title={data.primaryTrainRoute.title}
                displayStationAdForRoute={data.displayStationAdForRoute}
              />
            )}
            {getAdsConditionally()}
            {data.trainsEndingAtDestination && data.trainsEndingAtDestination.list.length > 0 && (
              <TrainInfoList
                title={data.trainsEndingAtDestination.title}
                trains={data.trainsEndingAtDestination.list}
              />
            )}
            {data.trainsStartingFromSource && data.trainsStartingFromSource.list.length > 0 && (
              <TrainInfoList title={data.trainsStartingFromSource.title} trains={data.trainsStartingFromSource.list} />
            )}
            <TrainStationAddress sourceStation={data.sourceStation} destinationStation={data.destinationStation} />
            <section className="railSeoWrap__stnDetails makeFlex" style={{ flexWrap: 'wrap' }}>
              <RecommendTripPathWrapper recommendPaths={RECOMMEND_PATH.TRAIN_INFO_PAGE(data)} />
            </section>
            <FaqSection
              faq={data.faq.list}
              pageUrl={data.pageUrl}
              title={data.faq.title}
              bookNowString={data.miscellaneous['BOOK NOW']}
              pageName={SEO_TRAIN_PAGE}
            />
            {data.geoFaqList && (
              <FaqSection
                faq={data.geoFaqList.list}
                pageUrl={data.pageUrl}
                title={data.geoFaqList.title}
                bookNowString={data.miscellaneous['BOOK NOW']}
                pageName={SEO_TRAIN_PAGE}
              />
            )}
          </main>
        </div>
      </div>
      <FooterSection template={data.template} />
    </TrainInfoProvider>
  );
}

TrainInfo.fetchData = async (_, match, urlObj) => {
  const {
    params: { trainId, templateId }
  } = match;
  const url = `${DATA_LOADER_URL}/api/getTrainTemplateData/${templateId}/${trainId}?${urlObj.query}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(`Unable to fetch train details of ${trainId}`, responseData.error);
  }
  try {
    return decorateTrainData(responseData);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error while formatting train data: ', error);
  }
};
export default TrainInfo;
